import { useTranslation } from 'react-i18next'
import type { LegalDoc } from '@/consumer/lib/legal-content'

/**
 * Renders a {@link LegalDoc} (the platform-default Terms & Conditions /
 * Privacy Policy) as consistent, readable prose. Shared by the public legal
 * pages and any in-app surface that needs to show the same content, so the two
 * never drift.
 */
export function LegalDocumentView({ doc }: { doc: LegalDoc }) {
  const { t } = useTranslation()

  return (
    <div className="text-[var(--ink-2)]">
      {doc.lastUpdated && (
        <p className="mb-6 text-xs text-[var(--ink-3)]">
          {t('legal.lastUpdated', 'Last updated: {{date}}', { date: doc.lastUpdated })}
        </p>
      )}

      {doc.blocks.map((block, i) => {
        if (block.kind === 'heading') {
          return (
            <h2
              key={i}
              className="mt-8 mb-2 font-display text-[20px] text-[var(--ink)] first:mt-0"
            >
              {block.text}
            </h2>
          )
        }
        if (block.kind === 'list') {
          return (
            <ul
              key={i}
              className="mb-4 list-disc space-y-1.5 pl-5 text-sm leading-relaxed"
            >
              {block.items.map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </ul>
          )
        }
        return (
          <p key={i} className="mb-4 text-sm leading-relaxed">
            {block.text}
          </p>
        )
      })}

      {doc.contact && (
        <p className="mt-8 border-t-[0.5px] border-t-[var(--rule)] pt-4 text-sm text-[var(--ink-3)]">
          {doc.contact}
        </p>
      )}
    </div>
  )
}
